'use client';

import { useState } from 'react';
import { useTranslations } from 'next-intl';
import { ChevronRight } from 'lucide-react';
import { SettingsCard, PickerRow } from '@/shared/ui';
import { ThumbStrip } from '../scrollable-settings/ReferenceSummary';
import { ProjectReferenceSheet } from '../scrollable-settings/ProjectReferenceSheet';
import {
  useClassifiedProjectReferences,
  useProjectReferences,
} from '@/features/project-reference/hooks/use-project-references';
import type { ProjectReferenceCategory } from '@/types/project-reference';

interface ReferenceSourceGroupProps {
  projectId: string;
  category: ProjectReferenceCategory;
  /** Manually selected project-reference ids (drives "Selective"). */
  selectedIds: string[];
  /** Whether "All project references" is active (the `use_auto_*_refs` flag). */
  useAllRefs: boolean;
  maxSelection: number;
  /** "No references": clear `use_auto_*_refs` + manual refs. Library untouched. */
  onSelectNone: () => void;
  /** "All project references": set `use_auto_*_refs` + clear manual refs + clear library. */
  onSelectAll: () => void;
  /** "Selective references": picked ids (also clears library in the caller). */
  onSelectiveChange: (ids: string[]) => void;
  onNavigateToReferences?: () => void;
}

/**
 * Unified "From reference" group for the Style/Materials/Colors sub-panels:
 * a single radio list of three mutually-exclusive options replacing the old
 * "Auto" segmented card (no refs / all refs) + the separate "From reference"
 * drill-down. Mirrors Apple `ReferenceSourceGroup` and Android
 * `ReferenceSourceGroup`.
 *
 * Selected option is derived from existing state — no new fields:
 *  - manual refs picked        → Selective
 *  - else `useAllRefs` true     → All project references
 *  - else                       → No references (default)
 */
export function ReferenceSourceGroup({
  projectId,
  category,
  selectedIds,
  useAllRefs,
  maxSelection,
  onSelectNone,
  onSelectAll,
  onSelectiveChange,
  onNavigateToReferences,
}: ReferenceSourceGroupProps) {
  const t = useTranslations('render.settings.projectReference');
  const [isSheetOpen, setIsSheetOpen] = useState(false);

  const { data: matchingReferences = [] } = useClassifiedProjectReferences(
    projectId,
    category,
    { enabled: !!projectId }
  );
  // "All project references" shows only this slot's category.
  const allRefImages = matchingReferences
    .map((r) => r.attributes.file_url)
    .filter((url): url is string => !!url);

  // Selected references resolve by id across ALL references (any category): the
  // picker lets users select refs from other/unclassified categories, so a
  // category-scoped lookup dropped their thumbnails.
  const { data: allRefs = [] } = useProjectReferences(projectId, {
    enabled: !!projectId,
  });
  const selectedRefImages = selectedIds
    .map((id) => allRefs.find((r) => r.id === id)?.attributes.file_url)
    .filter((url): url is string => !!url);

  const mode =
    selectedIds.length > 0 ? 'selective' : useAllRefs ? 'all' : 'none';

  return (
    <>
      <SettingsCard title={t('fromReference')}>
        <PickerRow
          title={t('none')}
          isSelected={mode === 'none'}
          onClick={onSelectNone}
        />
        <PickerRow
          title={t('all')}
          isSelected={mode === 'all'}
          onClick={onSelectAll}
          accessory={
            allRefImages.length > 0 ? (
              <ThumbStrip images={allRefImages} shape="circle" />
            ) : undefined
          }
        />
        <PickerRow
          title={t('selective')}
          isSelected={mode === 'selective'}
          onClick={() => setIsSheetOpen(true)}
          // Drill-down row (opens the reference picker) — always show a chevron
          // affordance, with the selected thumbnails before it.
          accessory={
            <div className="flex items-center gap-2">
              {selectedRefImages.length > 0 && (
                <ThumbStrip images={selectedRefImages} shape="circle" />
              )}
              <ChevronRight className="h-4 w-4 text-muted-foreground flex-shrink-0 rtl:-scale-x-100" />
            </div>
          }
        />
      </SettingsCard>

      <ProjectReferenceSheet
        projectId={projectId}
        category={category}
        selectedIds={selectedIds}
        onSelectionChange={onSelectiveChange}
        maxSelection={maxSelection}
        open={isSheetOpen}
        onOpenChange={setIsSheetOpen}
        onNavigateToReferences={onNavigateToReferences}
      />
    </>
  );
}
